'use client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useState } from 'react';
import { AgentLineAgent2 } from '@/app/components/icons/AgentLineAgent2';
import { AgentLineClaw } from '@/app/components/icons/AgentLineClaw';
import { BaseLinePackup } from '@/app/components/icons/BaseLinePackup';
import { BaseLineQuickEdit } from '@/app/components/icons/BaseLineQuickEdit';
import { ChannelBilibili } from '@/app/components/icons/ChannelBilibili';
import { ChannelDiscord2 } from '@/app/components/icons/ChannelDiscord2';
import { ChannelRed } from '@/app/components/icons/ChannelRed';
import { ChannelTt } from '@/app/components/icons/ChannelTt';
import { ChannelWechat2 } from '@/app/components/icons/ChannelWechat2';
import { ChannelX } from '@/app/components/icons/ChannelX';
import { ChannelYoutube } from '@/app/components/icons/ChannelYoutube';
import { LoginWhite } from '@/app/components/icons/LoginWhite';
import { LogoWhite } from '@/app/components/icons/LogoWhite';
import { ProductLineCreations } from '@/app/components/icons/ProductLineCreations';
import { ProductLineHome } from '@/app/components/icons/ProductLineHome';
import { ProductLineImage } from '@/app/components/icons/ProductLineImage';
import { ProductLineReference } from '@/app/components/icons/ProductLineReference';
import { ProductLineTemplates } from '@/app/components/icons/ProductLineTemplates';
import { ProductLineText } from '@/app/components/icons/ProductLineText';
import { ProductLineText2Image } from '@/app/components/icons/ProductLineText2Image';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { FeatureTour } from '@/app/components/shared/FeatureTour';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import {
  AD_SCENE_FROM_PAGE_MAP,
  HOME_PAGE,
  isEnterpriseDistribution,
  QRCODE_URL,
  SITE,
  TOUR_KEYS,
  TWITTER_LINK,
} from '@/app/constants';
import { PAGE_CONFIG_KEY, useGlobalModal, usePageConfigs, useUserNexus } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, isDistributionRouteVisible, reportEvent } from '@/app/utils';
import { BaseLineProfile } from '../../icons/BaseLineProfile';
import appIcon from './_assets/app.png';
import { MenuItem } from './components/MenuItem';
import type { SingleMenuItem } from './types';

const MENU_ITEM_ICON_CLASSNAME = 'size-5 text-system-white';

const BASE_MENU_DIVIDER_AFTER_IDS = ['generate-image', 'tools', 'assets'] as const;

const shouldShowWechat = SITE === 'china';
const WECHAT_LABEL = '微信群';
const DISCORD_LABEL = 'Discord';
const WECHAT_QR_TOOLTIP_CONTENT = (
  <div className="h-[248px] w-[216px] rounded-12 border border-system-white12 bg-system-bg02 px-2 py-3">
    <div className="mx-auto h-[200px] w-[200px]">
      <ImageWithPlaceholder src={QRCODE_URL} alt="微信群二维码" className="h-full w-full rounded-6 bg-[#3C4352]" />
    </div>
    <p className="mt-2 text-center font-[400] text-system-white64 text-xs tracking-[0]">加入讨论群，了解最新动态</p>
  </div>
);

const CN_SOCIAL_LINKS = isEnterpriseDistribution
  ? []
  : [
      ...(shouldShowWechat
        ? [
            {
              label: WECHAT_LABEL,
              icon: (
                <div className="flex items-center gap-2">
                  <ChannelWechat2 className="text-lg text-white" />
                  <span className="font-[400] text-system-white80 text-xs">{WECHAT_LABEL}</span>
                </div>
              ),
              href: '#',
            },
          ]
        : []),
      {
        label: 'Bilibili',
        icon: <ChannelBilibili className="text-lg" />,
        href: 'https://space.bilibili.com/3546707509906117',
      },
      {
        label: 'Xiaohongshu',
        icon: <ChannelRed className="text-lg" />,
        href: 'https://www.xiaohongshu.com/user/profile/66277a1a000000000303295b?xhsshare=CopyLink&appuid=5e5f08880000000001000cf4&apptime=1721633528&share_id=7232bad910cf4b1b8819394c5f886296',
      },
      {
        label: 'Douyin',
        icon: <ChannelTt className="text-lg" />,
        href: 'https://v.douyin.com/iMA3xoFV/',
      },
    ];

const EN_SOCIAL_LINKS = [
  {
    label: DISCORD_LABEL,
    icon: (
      <div className="flex items-center gap-2">
        <div className="rounded-6 bg-[#5865F2] p-[1px]">
          <ChannelDiscord2 className="text-lg text-white" />
        </div>
        <span className="font-[400] text-system-white80 text-xs">{DISCORD_LABEL}</span>
      </div>
    ),
    href: 'https://discord.gg/Ymeg4kDbmF',
  },
  { label: 'Twitter', icon: <ChannelX className="text-lg" />, href: TWITTER_LINK },
  {
    label: 'Youtube',
    icon: <ChannelYoutube className="text-lg" />,
    href: 'https://www.youtube.com/channel/UChF9964qVeqdSL9MkzBwCig',
  },
  { label: 'Tiktok', icon: <ChannelTt className="text-lg" />, href: 'https://www.tiktok.com/@vidu_ai?lang=en' },
];

const PCSidebar: React.FC = () => {
  const t = useTranslations();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [socialLinks, setSocialLinks] = useState<typeof CN_SOCIAL_LINKS | typeof EN_SOCIAL_LINKS>([]);
  const { getPageConfigs, configData } = usePageConfigs([PAGE_CONFIG_KEY.templateEntranceLabel]);
  const { open: openDownloadAppDialog } = useGlobalModal('downloadAppDialog');
  const { open: openGoogleRewardedAd } = useGlobalModal('googleRewardedAd');

  const { adWatchCreditEligible, isTeamSpace } = useStore((state) => ({
    adWatchCreditEligible: state.adWatchCreditEligible,
    isTeamSpace: state.isTeamSpace,
  }));
  const { isMissionWatchAdCredit } = useUserNexus();
  const isNexusEligible = SITE === 'global' && isMissionWatchAdCredit;
  const isEligible = isNexusEligible && adWatchCreditEligible;
  const menuDividerAfterIds = new Set<string>([
    ...(isTeamSpace ? ['team-profile'] : ['home']),
    ...BASE_MENU_DIVIDER_AFTER_IDS,
  ]);

  useEffect(() => {
    setSocialLinks(SITE === 'china' ? CN_SOCIAL_LINKS : EN_SOCIAL_LINKS);
    getPageConfigs();
  }, [getPageConfigs]);

  const menuItems: SingleMenuItem[] = [
    {
      name: t('sidebar.home'),
      shortName: t('sidebar.shortItems.home'),
      icon: <ProductLineHome className={MENU_ITEM_ICON_CLASSNAME} />,
      path: HOME_PAGE,
      id: 'home',
      onClick: () => reportEvent('element_click', { element_name: 'menu - home' }),
    },
    ...(isTeamSpace
      ? [
          {
            name: t('sidebar.teamProfile'),
            shortName: t('sidebar.shortItems.teamProfile'),
            icon: <BaseLineProfile className={MENU_ITEM_ICON_CLASSNAME} />,
            path: '/team-profile',
            id: 'team-profile',
            onClick: () => reportEvent('element_click', { element_name: 'menu - team-profile' }),
          } satisfies SingleMenuItem,
        ]
      : []),
    {
      name: t('sidebar.toolsItems.referenceToVideo'),
      shortName: t('sidebar.shortItems.referenceToVideo'),
      icon: <ProductLineReference className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/character2video',
      id: 'reference-to-video',
      onClick: () => reportEvent('element_click', { element_name: 'menu - reference-to-video' }),
    },
    {
      name: t('sidebar.toolsItems.imageToVideo'),
      shortName: t('sidebar.shortItems.imageToVideo'),
      icon: <ProductLineImage className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/img2video',
      id: 'image-to-video',
      onClick: () => reportEvent('element_click', { element_name: 'menu - image-to-video' }),
    },
    {
      name: t('sidebar.toolsItems.textToVideo'),
      shortName: t('sidebar.shortItems.textToVideo'),
      icon: <ProductLineText className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/text2video',
      id: 'text-to-video',
      onClick: () => reportEvent('element_click', { element_name: 'menu - text-to-video' }),
    },
    {
      name: t('sidebar.generateImage'),
      shortName: t('sidebar.shortItems.generateImage'),
      icon: <ProductLineText2Image className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/reference2image',
      activePaths: ['/create/text2image'],
      id: 'generate-image',
      onClick: () => reportEvent('element_click', { element_name: 'menu - generate-image' }),
    },
    {
      name: t('sidebar.tools'),
      shortName: t('sidebar.shortItems.tools'),
      icon: <BaseLineQuickEdit className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/tools',
      id: 'tools',
      onClick: () => reportEvent('element_click', { element_name: 'menu - tools' }),
    },
    {
      name: t('sidebar.assets'),
      shortName: t('sidebar.shortItems.assets'),
      icon: <ProductLineCreations className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/assets/mycreations',
      activePaths: ['/assets/myreferences', '/assets/liverecords'],
      id: 'assets',
      onClick: () => reportEvent('element_click', { element_name: 'menu - assets' }),
    },
    ...(isTeamSpace
      ? []
      : [
          {
            name: t('sidebar.labItems.claw'),
            shortName: t('sidebar.shortItems.claw'),
            icon: <AgentLineClaw className={MENU_ITEM_ICON_CLASSNAME} />,
            path: '/claw',
            id: 'claw',
            onClick: () => {
              reportEvent('element_click', { element_name: 'menu - claw' });
              reportEvent('claw_page_view', { page_type: 'claw_page', enter_from: 'home_page' });
            },
          } satisfies SingleMenuItem,
        ]),
    {
      name: t('sidebar.labItems.agent'),
      shortName: t('sidebar.shortItems.agent'),
      icon: <AgentLineAgent2 className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/project',
      id: 'agent',
      onClick: () => {
        reportEvent('element_click', { element_name: 'menu - agent' });
        reportEvent('project_page_view', { page_type: 'project_list_page', enter_from: 'home_page' });
      },
    },
    {
      name: t('sidebar.labItems.templates'),
      shortName: t('sidebar.shortItems.templates'),
      icon: <ProductLineTemplates className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/templates',
      tag: configData?.[PAGE_CONFIG_KEY.templateEntranceLabel],
      id: 'templates',
      onClick: () => reportEvent('templates_exposure', { refer_element: 'menu - templates' }),
    },
  ];
  const visibleMenuItems = menuItems.filter((item) => isDistributionRouteVisible(item.path));

  const handleToggleSidebar = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('vidu-sidebar-collapsed', `${next}`);
      return next;
    });
  };

  return (
    <aside
      className={cn(
        'group relative flex h-full flex-col justify-between bg-system-bg08 text-sm text-white transition-all md:hidden',
        isCollapsed ? 'ml-3 w-[56px] px-0 py-4' : 'ml-4 w-[224px] px-0 py-4',
      )}
      data-google-interstitial="false"
    >
      <div
        className={cn(
          'mt-1 mb-6 flex items-center',
          isCollapsed ? 'justify-center' : !isEnterpriseDistribution ? 'justify-between px-1' : 'justify-end px-1',
        )}
      >
        {isCollapsed ? (
          <div className="relative flex size-8 items-center justify-center">
            {!isEnterpriseDistribution && (
              <ClientLink
                href={HOME_PAGE}
                className="flex items-center justify-center transition-opacity group-hover:opacity-0"
              >
                <LogoWhite className="text-3xl" />
              </ClientLink>
            )}
            <CustomTooltip content={t('sidebar.expandMenu')} side="right" disableHoverableContent={true}>
              <button
                type="button"
                className={cn(
                  'items-center justify-center rounded-10 text-system-white64 transition-colors hover:bg-system-white08 hover:text-system-white',
                  !isEnterpriseDistribution ? 'absolute inset-0 hidden group-hover:flex' : 'flex size-8',
                )}
                onClick={handleToggleSidebar}
                aria-label={t('sidebar.expandMenu')}
                title={t('sidebar.expandMenu')}
              >
                <BaseLinePackup className="text-inherit text-lg" />
              </button>
            </CustomTooltip>
          </div>
        ) : (
          <>
            {!isEnterpriseDistribution && (
              <ClientLink href={HOME_PAGE} className="overflow-hidden">
                <LoginWhite className="text-2xl" />
              </ClientLink>
            )}
            <CustomTooltip content={t('sidebar.collapseMenu')} side="right" disableHoverableContent={true}>
              <button
                type="button"
                className="flex size-8 items-center justify-center rounded-10 text-system-white64 transition-colors hover:bg-system-white08 hover:text-system-white"
                onClick={handleToggleSidebar}
                aria-label={t('sidebar.collapseMenu')}
                title={t('sidebar.collapseMenu')}
              >
                <BaseLinePackup className="rotate-180 text-inherit text-lg" />
              </button>
            </CustomTooltip>
          </>
        )}
      </div>
      <nav className="hide-scrollbar flex-1 overflow-y-scroll pr-0.5">
        {visibleMenuItems.map((item) => {
          const shouldShowDivider = item.id && menuDividerAfterIds.has(item.id);
          return (
            <div key={item.name}>
              <div className="mb-2">
                <MenuItem
                  name={item.name}
                  icon={item.icon}
                  path={item.path}
                  activePaths={item.activePaths}
                  badge={item.badge}
                  id={item.id}
                  exact={item.exact}
                  collapsed={isCollapsed}
                  shortName={item.shortName}
                />
              </div>
              {shouldShowDivider && (
                <div className={cn('mb-3', isCollapsed ? 'flex justify-center' : 'px-3')}>
                  <div className={cn('h-px rounded-full bg-system-white08', isCollapsed ? 'w-6' : 'w-full')} />
                </div>
              )}
            </div>
          );
        })}
      </nav>
      {!isTeamSpace && (
        <FeatureTour
          tourKey={TOUR_KEYS.CLAW_ENTRY_TOUR}
          tourConfig={{
            items: [
              {
                domId: 'claw',
                side: 'right',
                align: 'center',
                sideOffset: 12,
                description: [t('claw.tour.entry.description')],
                popoverCtxClassName: 'w-[280px]',
                popoverCtxInnerClassName: 'pr-7',
                highlightClassName: 'tour-highlight-soft',
              },
            ],
          }}
        />
      )}
      {!isEnterpriseDistribution && (
        <div className={cn('flex flex-col', isCollapsed ? 'gap-4' : 'gap-2')}>
          {!isCollapsed && isEligible ? (
            <div
              className="relative flex h-16 w-full cursor-pointer flex-col justify-center overflow-hidden rounded-12 px-5 ring-1 ring-system-white20"
              onClick={() => {
                openGoogleRewardedAd({ scene: 'sidebar_bottom' });
                reportEvent('ad_click', { from_page: AD_SCENE_FROM_PAGE_MAP.sidebar_bottom, watch: 'sidebar bottom' });
              }}
            >
              <img
                src="https://image01.cf.vidu.studio/vidu/media-asset/watch_ads_guide-b9b8f89d.webp"
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="relative text-[10px] text-system-white leading-[100%] opacity-[.54]">
                {t('promptBanner.watchAdsGuide.description')}
              </div>
              <div className="relative mt-[3px] font-semibold text-base text-system-white leading-[100%]">
                {t('promptBanner.watchAdsGuide.title')}
              </div>
            </div>
          ) : !isCollapsed ? (
            <div
              className="w-full cursor-pointer overflow-hidden"
              onClick={() => {
                openDownloadAppDialog({ fromPage: 'create_page' });
                reportEvent('get_app_click', { click_position: 'create_page' });
              }}
            >
              <div
                className={cn(
                  'flex min-h-[66px] items-center rounded-[12px] bg-gradient-to-r from-[rgba(216,200,255,0.12)] to-[rgba(96,207,255,0.12)] px-4 py-3',
                  isCollapsed ? 'w-full' : 'w-[224px]',
                )}
              >
                <img src={appIcon.src} alt="Download App" className="mr-[10px] h-10 w-10 flex-shrink-0" />
                <div className="flex flex-col gap-1">
                  <div className="bg-gradient-to-r from-[#5AE0FF] to-[#009BFF] bg-clip-text font-semibold text-base text-transparent leading-[140%]">
                    {SITE === 'global' ? t('promptBanner.button.getApp') : '下载Vidu AI App'}
                  </div>
                  <div className="font-normal text-white/60 text-xs leading-4">{t('promptBanner.button.download')}</div>
                </div>
              </div>
            </div>
          ) : null}

          <div
            className={cn(
              'flex w-full gap-2',
              isCollapsed ? 'mt-4 flex-col items-center justify-center' : 'items-center justify-start',
            )}
          >
            {socialLinks.map(({ icon, href, label }) => {
              const socialButton = (
                <div
                  className={cn(
                    'rounded-full shadow-[0_0_12px_rgba(255,255,255,0.1)] transition-colors hover:bg-system-white24',
                    label === DISCORD_LABEL || label === WECHAT_LABEL ? 'bg-system-white12' : 'bg-system-white08',
                    isCollapsed ? 'flex size-7 items-center justify-center' : 'p-2',
                    isCollapsed && label === DISCORD_LABEL && 'bg-transparent shadow-none hover:bg-transparent',
                  )}
                >
                  {isCollapsed && label === DISCORD_LABEL ? (
                    <div className="flex size-5 items-center justify-center rounded-6 bg-[#5865F2]">
                      <ChannelDiscord2 className="text-base text-white" />
                    </div>
                  ) : isCollapsed && label === WECHAT_LABEL ? (
                    <ChannelWechat2 className="text-lg text-white" />
                  ) : (
                    icon
                  )}
                </div>
              );

              if (label === WECHAT_LABEL) {
                return (
                  <CustomTooltip
                    key={label}
                    content={WECHAT_QR_TOOLTIP_CONTENT}
                    side="top"
                    align={isCollapsed ? 'center' : 'start'}
                    sideOffset={8}
                    variant="extend"
                    contentClassName="border-none bg-transparent p-0 shadow-none"
                  >
                    <button
                      type="button"
                      title={label}
                      aria-label={label}
                      className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-system-white36"
                      onClick={() => {
                        reportEvent('element_click', { element_name: label });
                      }}
                    >
                      {socialButton}
                    </button>
                  </CustomTooltip>
                );
              }

              return (
                <ClientLink
                  key={label}
                  href={href}
                  target="_blank"
                  title={label}
                  rel="nofollow noreferrer"
                  className="cursor-pointer text-system-white36"
                  onClick={() => {
                    reportEvent('element_click', { element_name: label });
                  }}
                >
                  {socialButton}
                </ClientLink>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
};

export default PCSidebar;
